import { useState, useEffect, useRef, useCallback } from 'react';
import { isEmpty } from 'lodash';

import { EBON_USER_STATE_USED, WAITING_STOCK } from '../constants/ebon';

import {
    getOrderStatus,
    formatAmountWithComma,
    buildEbonUserStatePayload
} from '../utils/ebonStatusUtils';

export const useEbonOrder = ({
    id,
    ebonOrderWithCodes,
    ebonListFromProps,
    fetchAccountEbonOrderByIdWithCode,
    updateEbonUserState,
    state
}) => {
    const [open, setOpen] = useState(false);
    const [showHowTo, setshowHowTo] = useState(false);
    const [ebonStatus, setEbonStatus] = useState('');
    const [remainingBalance, setRemainingBalance] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useRef(true);

    const ebonList = (ebonOrderWithCodes && ebonOrderWithCodes.ebonList) || ebonListFromProps || [];
    const stateOk = state !== WAITING_STOCK;

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (open && stateOk && isEmpty(ebonOrderWithCodes) && isMounted.current) {
            fetchAccountEbonOrderByIdWithCode(id);
        }
    }, [open, ebonOrderWithCodes, id, fetchAccountEbonOrderByIdWithCode]);

    useEffect(() => {
        if (isMounted.current && ebonList) {
            const totalAmount = ebonList.reduce((sum, ebon) => sum + (ebon.facialValue || 0), 0);
            const orderStatus = getOrderStatus(ebonList, totalAmount);
            if (orderStatus.status) {
                setEbonStatus(orderStatus.status);
                if (orderStatus.remainingBalance !== null && !isNaN(orderStatus.remainingBalance)) {
                    setRemainingBalance(formatAmountWithComma(orderStatus.remainingBalance));
                }
            }
        }
    }, [ebonList]);

    const handleSubmitStatus = useCallback(
        async (selectedEbonIdOrStatus, statusOrBalance, balance) => {
            if (!ebonList || ebonList.length === 0) {
                console.error('No eBon to update');
                return;
            }

            setSubmitting(true);

            try {
                let ebonUserStates;

                if (ebonList.length === 1) {
                    const status = selectedEbonIdOrStatus;
                    const balanceValue = statusOrBalance;
                    let finalStatus = status;
                    let finalBalance = balanceValue;

                    if (balanceValue) {
                        const parsedBalance = parseFloat(balanceValue.replace(',', '.'));
                        if (!isNaN(parsedBalance) && parsedBalance === 0) {
                            finalStatus = EBON_USER_STATE_USED;
                            finalBalance = '0';
                        }
                    }

                    ebonUserStates = buildEbonUserStatePayload(ebonList, finalStatus, finalBalance);
                } else {
                    const selectedEbonId = selectedEbonIdOrStatus;
                    const status = statusOrBalance;
                    const selectedEbon = ebonList.find(ebon => String(ebon.id) === selectedEbonId);

                    if (!selectedEbon) {
                        console.error(
                            'Selected eBon not found. selectedEbonId:',
                            selectedEbonId,
                            'ebonList:',
                            ebonList.map(e => e.id)
                        );
                        return;
                    }

                    let finalStatus = status;
                    let finalBalance = balance;

                    if (balance) {
                        const parsedBalance = parseFloat(balance.replace(',', '.'));
                        if (!isNaN(parsedBalance) && parsedBalance === 0) {
                            finalStatus = EBON_USER_STATE_USED;
                            finalBalance = '0';
                        }
                    }

                    const balanceAmount = finalBalance
                        ? parseFloat(finalBalance.replace(',', '.'))
                        : selectedEbon.facialValue;

                    ebonUserStates = [
                        {
                            id: selectedEbon.id,
                            userState: finalStatus,
                            remainingBalance: balanceAmount
                        }
                    ];
                }

                await updateEbonUserState(ebonUserStates);

                if (isMounted.current) {
                    await fetchAccountEbonOrderByIdWithCode(id);
                    console.log('eBon status updated successfully');
                }
            } catch (error) {
                console.error('Error updating eBon status:', error);
            } finally {
                if (isMounted.current) {
                    setSubmitting(false);
                }
            }
        },
        [ebonList, id, updateEbonUserState, fetchAccountEbonOrderByIdWithCode]
    );

    const toggleOpen = useCallback(() => {
        setOpen(prev => !prev);
    }, []);

    const toggleShowHowTo = useCallback(() => {
        setshowHowTo(prev => !prev);
    }, []);

    const totalAmount = ebonList.reduce((sum, ebon) => sum + (ebon.facialValue || 0), 0);
    const orderStatus = getOrderStatus(ebonList, totalAmount);

    return {
        open,
        setOpen: toggleOpen,
        showHowTo,
        setshowHowTo: toggleShowHowTo,
        ebonStatus,
        remainingBalance,
        submitting,
        orderStatus,
        ebonList,
        handleSubmitStatus,
        stateOk
    };
};



// WEBPACK FOOTER //
// ./src/hooks/useEbonOrder.js